import type { PropertyCalendarOrphanGap, PropertyCalendarOrphanGapsLayer } from '@/api/client'
import { parseCalendarDate } from '@/lib/calendar-date'

/**
 * Presentation rules for the calendar's orphan-gaps layer.
 *
 * Every night, price, name and message is the stored scan's, returned by
 * admin-api. Nothing here works out a gap, a price or an eligibility: the
 * functions name, filter and format what came back.
 */

const OPEN_PREFIX = 'gap:'
const OPEN_PATTERN = /^gap:(\d{4}-\d{2}-\d{2})$/

/**
 * What `?open=` holds for a gap sheet: `gap:<first night>`. Gaps on one
 * property never overlap, so the first night names exactly one of them.
 */
export function gapOpenValue(firstNight: string): string {
  return `${OPEN_PREFIX}${firstNight}`
}

export function parseGapOpen(raw: string | null): string | null {
  const match = raw ? OPEN_PATTERN.exec(raw) : null
  return match ? match[1] : null
}

/**
 * Whether any night of the gap is in `month` (`YYYY-MM`). The read returns a
 * gap whole, so one that crosses a month edge is listed (and drawn) for both
 * months, and counts once in each.
 */
export function gapTouchesMonth(gap: PropertyCalendarOrphanGap, month: string): boolean {
  return gap.first_night.slice(0, 7) <= month && gap.last_night.slice(0, 7) >= month
}

/** "Oct 14", or "Wed, Oct 14, 2026" with `long`; the raw value when it is not a date. */
export function formatGapDay(day: string, { long = false } = {}): string {
  const date = parseCalendarDate(day)
  if (!date) return day
  return date.toLocaleDateString(
    'en-US',
    long
      ? { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' }
  )
}

export function nightsLabel(nights: number): string {
  return `${nights} ${nights === 1 ? 'night' : 'nights'}`
}

/** "3 nights, 14 Oct to 16 Oct (last night)" - the span in words, for a bar's tooltip and name. */
export function describeGapSpan(gap: PropertyCalendarOrphanGap): string {
  const first = formatGapDay(gap.first_night)
  const last = formatGapDay(gap.last_night)
  return gap.first_night === gap.last_night
    ? `${nightsLabel(gap.nights)}, ${first}`
    : `${nightsLabel(gap.nights)}, ${first} to ${last}`
}

/**
 * The moment the snapshot was computed, in the property's own zone ("Oct 13,
 * 8:04 AM EDT"). An instant that does not parse is shown as it came.
 */
export function formatSnapshotTime(asOf: string, timeZone: string): string {
  const at = new Date(asOf)
  if (Number.isNaN(at.getTime())) return asOf
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }
  try {
    return at.toLocaleString('en-US', { ...options, timeZone })
  } catch {
    // An unknown zone name: say the time in UTC rather than in the viewer's zone.
    return at.toLocaleString('en-US', { ...options, timeZone: 'UTC' })
  }
}

/**
 * Why an `ok` gaps layer should not be read as a clean "these are the gaps":
 * plain sentences, empty when the snapshot is current and complete. The
 * toolbar and the sheet both show them, so a stale answer says so wherever it
 * is used.
 */
export function snapshotCaveats(
  layer: Extract<PropertyCalendarOrphanGapsLayer, { status: 'ok' }>,
  timeZone: string
): string[] {
  const caveats: string[] = []
  if (layer.stale) {
    caveats.push(
      `Gaps may be out of date: the daily scan has not refreshed them since ${formatSnapshotTime(layer.as_of, timeZone)}.`
    )
  }
  if (layer.incomplete) {
    caveats.push('The last scan was cut short, so some gaps may be missing.')
  }
  return caveats
}
